import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Button, Alert, Tabs, Tab,
} from '@mui/material';
import { AdminPanelSettings as AdminIcon } from '@mui/icons-material';
import Navigation from '../components/Navigation';
import RoleSwitcherBar from '../components/RoleSwitcherBar';
import AdminStatsCards from '../components/admin/AdminStatsCards';
import AdminProjectTiles from '../components/admin/AdminProjectTiles';
import AdminProjectTable from '../components/admin/AdminProjectTable';
import AdminUsersTable from '../components/admin/AdminUsersTable';
import AdminDisputeCenter from '../components/admin/AdminDisputeCenter';
import AdminEstimatesTable from '../components/admin/AdminEstimatesTable';

const AdminDashboard = () => {
  const navigate = useNavigate();

  // ── Data ──────────────────────────────────────────────────────────────────
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [homeownersList, setHomeownersList] = useState([]);
  const [estimates, setEstimates] = useState([]);

  // ── UI ────────────────────────────────────────────────────────────────────
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [stats, setStats] = useState({
    totalProjects: 0, totalItems: 0, pendingApprovals: 0, approvedItems: 0, rejectedItems: 0,
  });

  // ── Filters ───────────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [estimateSearchQuery, setEstimateSearchQuery] = useState('');
  const [projectFilters, setProjectFilters] = useState({ status: 'all', sort: 'created_at', order: 'desc' });
  const [estimateFilters, setEstimateFilters] = useState({ status: 'all', sort: 'created_at', order: 'desc', sort_cost: true });

  // ── Fetch logic ───────────────────────────────────────────────────────────
  const fetchProjects = async () => {
    setProjectsLoading(true);
    try {
      const response = await axios.get('/api/projects', {
        params: {
          q: searchQuery || undefined,
          status: projectFilters.status !== 'all' ? projectFilters.status : undefined,
          sort: projectFilters.sort,
          order: projectFilters.order,
        },
      });
      const projectsData = response.data.projects;
      setProjects(projectsData);

      const budgetResults = await Promise.all(
        projectsData.map(p => axios.get(`/api/projects/${p.id}/budget`).catch(() => null))
      );

      let totalItems = 0, pendingApprovals = 0, approvedItems = 0, rejectedItems = 0;
      for (const result of budgetResults) {
        if (!result) continue;
        const b = result.data.budget;
        totalItems += parseInt(b.total_items) || 0;
        pendingApprovals += parseInt(b.outstanding_items) || 0;
        approvedItems += parseInt(b.admin_approved_items) || 0;
        rejectedItems += parseInt(b.rejected_items) || 0;
      }

      setStats({ totalProjects: projectsData.length, totalItems, pendingApprovals, approvedItems, rejectedItems });
    } catch (err) {
      setError('Failed to load projects');
    } finally {
      setProjectsLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/auth/users');
      setUsers(response.data.users);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchDisputes = async () => {
    try {
      const response = await axios.get('/api/items/disputes');
      setDisputes(response.data.disputes);
    } catch (err) {
      console.error('Error fetching disputes:', err);
    }
  };

  const fetchEstimates = async () => {
    try {
      const response = await axios.get('/api/estimates', {
        params: {
          q: estimateSearchQuery || undefined,
          status: estimateFilters.status !== 'all' ? estimateFilters.status : undefined,
          sort: estimateFilters.sort,
          order: estimateFilters.order,
        },
      });
      setEstimates(response.data.estimates || []);
    } catch (err) {
      setError('Failed to load estimates: ' + (err.response?.data?.error || err.message));
    }
  };

  const loadHomeowners = async () => {
    try {
      const response = await axios.get('/api/projects/homeowners');
      setHomeownersList(response.data.homeowners || []);
    } catch (err) {
      console.error('Error loading homeowners:', err);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      const response = await axios.delete(`/api/auth/users/${userId}`);
      setSuccessMessage(response.data.message);
      fetchUsers();
      loadHomeowners();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleAssignHomeowner = async (projectId, homeownerId) => {
    try {
      await axios.post(`/api/projects/${projectId}/assign-homeowner`, { homeownerId });
      fetchProjects();
      fetchEstimates();
      setSuccessMessage('Homeowner assigned successfully');
    } catch (err) {
      setError('Failed to assign homeowner');
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchDisputes();
    loadHomeowners();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchProjects(); }, [searchQuery, projectFilters]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { fetchEstimates(); }, [estimateSearchQuery, estimateFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Navigation showNotifications={false} showLogout={true} />
      <RoleSwitcherBar />

      <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 3, md: 4 }, mb: 4, px: { xs: 2, sm: 3 } }}>
        <Box mb={4} display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" gutterBottom>Welcome, Admin</Typography>
            <Typography variant="body1" color="text.secondary">
              Oversee all projects and provide final approvals
            </Typography>
          </Box>
          <Button variant="outlined" color="primary" onClick={() => navigate('/system-monitor')} startIcon={<AdminIcon />}>
            System Monitor
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>
        )}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>{successMessage}</Alert>
        )}

        <AdminStatsCards stats={stats} onTabChange={setActiveTab} />

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
            <Tab label="Project Tiles" />
            <Tab label="Project Table" />
            <Tab label="Users" />
            <Tab label={`Dispute Center${disputes.length > 0 ? ` (${disputes.length})` : ''}`} />
            <Tab label={`Estimates & Signatures${estimates.length > 0 ? ` (${estimates.length})` : ''}`} />
          </Tabs>
        </Box>

        {activeTab === 0 && (
          <AdminProjectTiles
            projects={projects}
            loading={projectsLoading}
            searchQuery={searchQuery}
            onSearch={setSearchQuery}
            filters={projectFilters}
            onFilterChange={setProjectFilters}
            onUpdate={fetchProjects}
          />
        )}

        {activeTab === 1 && (
          <AdminProjectTable
            projects={projects}
            loading={projectsLoading}
            searchQuery={searchQuery}
            onSearch={setSearchQuery}
            filters={projectFilters}
            onFilterChange={setProjectFilters}
            homeownersList={homeownersList}
            onAssignHomeowner={handleAssignHomeowner}
          />
        )}

        {activeTab === 2 && <AdminUsersTable users={users} onDeleteUser={handleDeleteUser} />}

        {activeTab === 3 && (
          <AdminDisputeCenter
            disputes={disputes}
            onResolved={() => { fetchDisputes(); setSuccessMessage('Dispute resolved successfully'); }}
          />
        )}

        {activeTab === 4 && (
          <AdminEstimatesTable
            estimates={estimates}
            searchQuery={estimateSearchQuery}
            onSearch={setEstimateSearchQuery}
            filters={estimateFilters}
            onFilterChange={setEstimateFilters}
            homeownersList={homeownersList}
            onAssignHomeowner={handleAssignHomeowner}
          />
        )}
      </Container>
    </Box>
  );
};

export default AdminDashboard;
